/**
 * Context bind menu for selecting a Client binding key from text selection.
 */

import { CLIENT_LINK_BINDINGS, CLIENT_TEXT_BINDINGS } from '../shared/client-info.js';
import { openPopoverMenu } from '../ui/popover-menu.js';
import { escapeHTML as escapeHtml } from '../shared/html-escape.js';

let menuEl = null;
let currentAnchorRect = null;
let onSelectCb = null;
let onCloseCb = null;
let restoreFocusCb = null;
let extraItemsCb = null; // optional list of { label, onSelect } rendered as a tools section
let _handle = null;

function normalizeAnchorRect(anchorRect) {
  if (!anchorRect) return null;
  const left = Number(anchorRect.left || 0);
  const top = Number(anchorRect.top || 0);
  const width = Number(anchorRect.width || 0);
  const height = Number(anchorRect.height || 0);
  return {
    left,
    top,
    width,
    height,
    right: Number(anchorRect.right ?? left + width),
    bottom: Number(anchorRect.bottom ?? top + height)
  };
}

function getDefaultOptions() {
  const merged = new Map();
  for (const binding of CLIENT_TEXT_BINDINGS) {
    merged.set(binding.key, { key: binding.key, label: binding.label });
  }
  for (const binding of CLIENT_LINK_BINDINGS) {
    if (!merged.has(binding.key)) {
      merged.set(binding.key, { key: binding.key, label: binding.label });
    }
  }
  return [...merged.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function buildBindMenuFragment({ items, selectionText, extraItems, close }) {
  const cappedSelection = String(selectionText || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  const subtitle = cappedSelection ? `"${escapeHtml(cappedSelection)}"` : 'Selected text';
  const fragment = document.createDocumentFragment();
  const root = document.createElement('div');
  root.className = 'pb-bind-menu-content';
  const extraSection =
    Array.isArray(extraItems) && extraItems.length
      ? `
      <div class="pb-bind-menu-divider" role="separator"></div>
      <div class="pb-bind-menu-tools">
        ${extraItems
          .map(
            (item, i) => `
          <button type="button" class="pb-bind-menu-item pb-bind-menu-tool" role="menuitem" data-tool-index="${i}">
            ${escapeHtml(item.label)}
          </button>
        `
          )
          .join('')}
      </div>
    `
      : '';
  root.innerHTML = `
    <div class="pb-bind-menu-head">Bind To Client Info</div>
    <div class="pb-bind-menu-sub">${subtitle}</div>
    <div class="pb-bind-menu-list">
      ${items
        .map(
          (item) => `
        <button type="button" class="pb-bind-menu-item" role="menuitem" data-key="${escapeHtml(item.key)}">
          ${escapeHtml(item.label)}
        </button>
      `
        )
        .join('')}
    </div>
    ${extraSection}
  `;

  root.addEventListener('click', (event) => {
    const tool = event.target.closest('.pb-bind-menu-tool[data-tool-index]');
    if (tool && Array.isArray(extraItems)) {
      const idx = Number(tool.dataset.toolIndex);
      if (
        Number.isInteger(idx) &&
        extraItems[idx] &&
        typeof extraItems[idx].onSelect === 'function'
      ) {
        if (extraItems[idx].skipRestoreFocus) restoreFocusCb = null;
        extraItems[idx].onSelect();
      }
      close();
      return;
    }
    const item = event.target.closest('.pb-bind-menu-item[data-key]');
    if (!item) return;
    const key = item.dataset.key || '';
    const chosen = items.find((entry) => entry.key === key) || null;
    if (chosen && typeof onSelectCb === 'function') {
      onSelectCb(chosen);
    }
    close();
  });

  fragment.appendChild(root);
  return fragment;
}

function clearStateAfterClose() {
  const cb = onCloseCb;
  menuEl = null;
  currentAnchorRect = null;
  onSelectCb = null;
  onCloseCb = null;
  restoreFocusCb = null;
  extraItemsCb = null;
  _handle = null;
  if (typeof cb === 'function') cb();
}

export function closeBindMenu() {
  if (_handle) {
    _handle();
    return;
  }
  clearStateAfterClose();
}

export function isBindMenuOpen() {
  return !!menuEl;
}

export function positionBindMenu(anchorRect) {
  if (anchorRect) currentAnchorRect = normalizeAnchorRect(anchorRect);
  _handle?.reposition?.();
}

export function openBindMenu({
  anchorRect,
  selectionText = '',
  options = null,
  extraItems = null,
  onSelect = null,
  onClose = null,
  restoreFocus = null
}) {
  closeBindMenu();

  const items = Array.isArray(options) && options.length ? options : getDefaultOptions();
  currentAnchorRect = normalizeAnchorRect(anchorRect);
  if (!items.length || !currentAnchorRect) return;

  onSelectCb = onSelect;
  onCloseCb = onClose;
  restoreFocusCb = restoreFocus;
  extraItemsCb = Array.isArray(extraItems) ? extraItems : null;

  _handle = openPopoverMenu(null, {
    owner: 'iframe-text-edit',
    className: 'pb-bind-menu',
    useBaseClass: false,
    align: 'left',
    gap: 8,
    menuLabel: 'Bind selected text to client info',
    getAnchorRect: () => currentAnchorRect,
    consumeCanvasDismiss: false,
    render: ({ close, menu }) => {
      menuEl = menu;
      return buildBindMenuFragment({ items, selectionText, extraItems: extraItemsCb, close });
    },
    restoreFocus: () => {
      if (typeof restoreFocusCb === 'function') restoreFocusCb();
    },
    onClose: clearStateAfterClose
  });
}
